import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

export default function Limbo() {
  const [wallet, setWallet] = useState({ balance_usdt: 0 });
  const [amount, setAmount] = useState("5");
  const [target, setTarget] = useState("2.00");
  const [busy, setBusy] = useState(false);
  const [display, setDisplay] = useState(1.0);
  const [last, setLast] = useState(null);
  const [history, setHistory] = useState([]);
  const animRef = useRef(null);

  useEffect(() => {
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    api.get("/limbo/history").then(({ data }) => setHistory(data)).catch(() => {});
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  const t = parseFloat(target) || 0;
  const amt = parseFloat(amount) || 0;
  const winChance = t >= 1.01 ? Math.min(97, 97 / t) : 0;

  const play = async () => {
    setBusy(true);
    setLast(null);
    try {
      const { data } = await api.post("/limbo/play", { amount: amt, target: t });
      const start = performance.now();
      const dur = 900;
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(1 + (data.result - 1) * eased);
        if (p < 1) {
          animRef.current = requestAnimationFrame(step);
        } else {
          setLast(data);
          setWallet((w) => ({ ...w, balance_usdt: data.balance_usdt }));
          setHistory((h) => [data, ...h].slice(0, 20));
          setBusy(false);
          if (data.won) toast.success(`WIN +${data.win.toFixed(2)} USDT at ${data.result}×`);
          else toast.error(`Busted at ${data.result}× — target was ${data.target}×`);
        }
      };
      animRef.current = requestAnimationFrame(step);
    } catch (err) {
      toast.error(apiError(err, "Play failed"));
      setBusy(false);
    }
  };

  const resultColor = busy
    ? "text-neon-cyan glow-cyan"
    : last
      ? last.won ? "text-neon-green glow-green" : "text-neon-red glow-red"
      : "text-white/80";

  return (
    <div className="min-h-[calc(100vh-4rem)] relative scanlines" data-testid="limbo-page">
      <div className="max-w-7xl mx-auto p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 relative z-10">
        <div className="lg:col-span-3 space-y-4">
          <div className="panel p-6 min-h-[380px] flex flex-col" data-testid="limbo-display">
            <div className="flex items-center justify-between">
              <div className="label-mono flex items-center gap-2"><span className="dot-live" /> LIMBO</div>
              <div className="label-mono text-white/40">instant · provably fair</div>
            </div>
            <div className="flex-1 grid place-items-center py-10">
              <div className="text-center">
                <div className="label-mono mb-2">{busy ? "Rolling…" : last ? (last.won ? "WIN" : "BUST") : "Result"}</div>
                <div className={`font-display font-black text-7xl lg:text-9xl font-mono leading-none ${resultColor}`} data-testid="limbo-result-value">
                  {display.toFixed(2)}×
                </div>
                {last && (
                  <div className="label-mono mt-4" data-testid="limbo-result-detail">
                    target {last.target}× · {last.won ? `+${last.win.toFixed(2)} USDT` : `-${last.amount.toFixed(2)} USDT`}
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="label-mono mb-2">// your_recent_rolls</div>
              <div className="flex flex-wrap gap-1.5" data-testid="limbo-history-strip">
                {history.length === 0 && <span className="text-white/40 text-xs font-mono">No rolls yet — set a target and play.</span>}
                {history.map((h) => (
                  <span key={h.play_id} className={`font-mono text-xs px-2 py-1 border ${h.won ? "text-neon-green border-neon-green/50" : "text-neon-red border-neon-red/50"}`}>
                    {h.result.toFixed(2)}×
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="panel p-5" data-testid="limbo-how-to">
            <div className="label-mono mb-4">// how_to_play</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="border border-neon-cyan/40 bg-black/40 p-4">
                <div className="font-mono text-xs font-bold text-neon-cyan">01</div>
                <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">Pick a target</div>
                <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">Choose a target multiplier (1.01× – 1000×). Higher target = bigger payout, lower chance.</p>
              </div>
              <div className="border border-neon-green/40 bg-black/40 p-4">
                <div className="font-mono text-xs font-bold text-neon-green">02</div>
                <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">Roll instantly</div>
                <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">Hit Play — a provably-fair number is generated instantly. No waiting for rounds.</p>
              </div>
              <div className="border border-neon-red/40 bg-black/40 p-4">
                <div className="font-mono text-xs font-bold text-neon-red">03</div>
                <div className="font-display font-bold text-sm uppercase tracking-wide mt-2">Beat the target</div>
                <p className="text-white/50 text-xs mt-1.5 leading-relaxed font-body">If the result is at or above your target you win entry × target. Below it, the entry is lost.</p>
              </div>
            </div>
          </div>

          {last && (
            <div className="panel p-4 font-mono text-xs space-y-1.5" data-testid="limbo-fairness">
              <div className="label-mono mb-2">// provably_fair · last roll</div>
              <div className="text-white/40">seed hash: <span className="text-neon-cyan break-all">{last.server_seed_hash}</span></div>
              <div className="text-white/40">seed: <span className="text-white/70 break-all">{last.server_seed}</span></div>
              <div className="text-white/40">result = max(1.00, (1 − edge) / (1 − int(SHA-256(seed)[0:13], 16) / 2^52))</div>
            </div>
          )}
        </div>

        <aside className="space-y-4">
          <div className="panel p-5" data-testid="limbo-wallet-mini">
            <div className="label-mono">Wallet</div>
            <div className="font-mono text-2xl font-bold mt-1 text-neon-green glow-green" data-testid="limbo-wallet-balance">
              {wallet.balance_usdt?.toFixed(2)} <span className="text-xs text-white/50">USDT</span>
            </div>
          </div>

          <div className="panel p-5 space-y-4" data-testid="limbo-entry-panel">
            <div className="label-mono">Entry Panel</div>
            <div>
              <label className="label-mono">Amount (USDT) · min 5</label>
              <input type="number" min="5" step="1" className="field mt-2" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="limbo-amount-input" />
              <div className="grid grid-cols-4 gap-1.5 mt-2">
                {[5, 10, 25, 50].map((v) => (
                  <button key={v} className="panel py-1.5 text-xs font-mono hover:border-neon-green transition-colors" onClick={() => setAmount(String(v))} data-testid={`limbo-quick-${v}`}>{v}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-mono">Target Multiplier</label>
              <input type="number" min="1.01" max="1000" step="0.01" className="field mt-2" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="limbo-target-input" />
              <div className="grid grid-cols-4 gap-1.5 mt-2">
                {[1.5, 2, 5, 10].map((v) => (
                  <button key={v} className="panel py-1.5 text-xs font-mono hover:border-neon-cyan transition-colors" onClick={() => setTarget(v.toFixed(2))} data-testid={`limbo-quick-target-${v}`}>{v}×</button>
                ))}
              </div>
            </div>
            <div className="font-mono text-xs space-y-1 text-white/50">
              <div className="flex justify-between"><span>Win chance</span><span className="text-neon-cyan">≈ {winChance.toFixed(2)}%</span></div>
              <div className="flex justify-between"><span>Payout on win</span><span className="text-neon-green">{(amt * t).toFixed(2)} USDT</span></div>
            </div>
            <button onClick={play} disabled={busy || amt < 5 || t < 1.01} className="btn-neon w-full" data-testid="limbo-play-button">
              {busy ? "ROLLING…" : "PLAY"}
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}
